// src/components/PatientDetails/ExternalIdEditModal.tsx
// -----------------------------------------------------------------------------
// Small modal to edit a patient's External ID(s) from the patient-details
// header. external_id is a single field on the Patient record that stores one
// or several hospital identifiers (NHC / MRN / …) comma-separated. We PATCH
// only the `external_id` field (partial update) so environments and other
// attributes are never touched.
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useSnackbar } from "notistack";
import { updateData } from "../../api/apiHelpers";
import { useTranslation } from "../../i18n";
import {
  athenaDarkPaperSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaDarkActionsSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
} from "../layout/AthenaDialogStyles";

export interface ExternalIdEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string | number;
  currentValue?: string;
  /** Called with the saved value so the parent can update its display. */
  onSaved: (newValue: string) => void;
}

const ExternalIdEditModal: React.FC<ExternalIdEditModalProps> = ({
  open,
  onClose,
  patientId,
  currentValue = "",
  onSaved,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // Internal model: list of tokens. The backend wire format remains a single
  // comma-joined string — `value.trim()` below joins them back. The UI shows
  // each token as a chip with × so users can see at a glance whether they are
  // adding a new ID or appending digits to an existing one. Joan, 2026-06-04.
  const initialTokens = useMemo(
    () =>
      (currentValue || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [currentValue],
  );
  const [tokens, setTokens] = useState<string[]>(initialTokens);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // Re-seed the field each time the modal opens for a (possibly new) value.
  useEffect(() => {
    if (open) {
      setTokens(initialTokens);
      setDraft("");
    }
  }, [open, initialTokens]);

  const handleSave = async () => {
    // Flush any uncommitted draft into a chip first, then join with commas.
    const pending = draft.trim();
    const allTokens = pending ? [...tokens, pending] : tokens;
    const trimmed = allTokens.join(", ");
    setSaving(true);
    try {
      // Partial PATCH — only external_id is sent, so environments and other
      // fields are untouched.
      await updateData(`/patients/${patientId}/`, { external_id: trimmed });
      enqueueSnackbar(
        t("patient.external_id_saved", "External ID updated."),
        { variant: "success", autoHideDuration: 3000 },
      );
      onSaved(trimmed);
      onClose();
    } catch (err) {
      enqueueSnackbar(
        t("patient.external_id_save_error", "Could not update the External ID."),
        { variant: "error", autoHideDuration: 5000 },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: athenaDarkPaperSx }}
    >
      <DialogTitle sx={athenaDarkTitleSx}>
        <BadgeOutlinedIcon fontSize="small" />
        {t("patient.edit_external_id_title", "Edit External ID")}
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={athenaDarkContentSx}>
        <Autocomplete<string, true, false, true>
          multiple
          freeSolo
          fullWidth
          size="small"
          // Top margin so the floating "External ID(s)" label clears the
          // dialog content's top edge instead of being clipped (the dark
          // content has only pt:1). cursor_logs/036 §25.
          sx={{ mt: 1 }}
          options={[]}
          value={tokens}
          inputValue={draft}
          onInputChange={(_, v, reason) => {
            // The MUI internal reset (option pick / chip commit) fires
            // reason="reset" with empty v — let that through so the input
            // empties properly. Otherwise mirror the typed text.
            if (reason === "reset") {
              setDraft(v);
              return;
            }
            // Auto-commit on any delimiter — comma OR whitespace. External IDs
            // never contain spaces, so "A123 B456" and "A123, B456" both land
            // as two chips (standard tag-input convention, faster to type).
            if (/[,\s]/.test(v)) {
              const parts = v.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
              if (parts.length === 0) {
                setDraft("");
                return;
              }
              setTokens((prev) => {
                const seen = new Set(prev);
                const next = [...prev];
                for (const p of parts) {
                  if (!seen.has(p)) {
                    seen.add(p);
                    next.push(p);
                  }
                }
                return next;
              });
              setDraft("");
              return;
            }
            setDraft(v);
          }}
          onChange={(_, newValue) => {
            // newValue comes through as (string | option)[]; freeSolo means
            // typed tokens arrive as raw strings. Dedup against existing.
            const cleaned: string[] = [];
            const seen = new Set<string>();
            for (const v of newValue) {
              const t = typeof v === "string" ? v.trim() : "";
              if (!t || seen.has(t)) continue;
              seen.add(t);
              cleaned.push(t);
            }
            setTokens(cleaned);
          }}
          disabled={saving}
          renderTags={(value, getTagProps) =>
            value.map((token, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${token}-${index}`}
                label={token}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label={t("patient.external_id_field_label", "External ID(s)")}
              placeholder={
                tokens.length === 0
                  ? t(
                      "patient.external_id_chip_placeholder",
                      "Type an ID; space, comma or Enter adds it",
                    )
                  : ""
              }
              onKeyDown={(e) => {
                // Enter on empty draft → save the modal.
                if (e.key === "Enter" && !draft.trim()) {
                  e.preventDefault();
                  if (!saving) handleSave();
                  return;
                }
                // Enter with text → commit the token (handled by MUI default).
                // Backspace on empty input pops the last chip (MUI default).
              }}
            />
          )}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {t(
            "patient.external_id_field_helper_chips",
            "Each ID becomes its own chip. Space, comma or Enter adds, × or Backspace removes.",
          )}
        </Typography>
      </DialogContent>
      <DialogActions sx={athenaDarkActionsSx}>
        <Button onClick={onClose} disabled={saving} sx={athenaDarkSecondaryButtonSx}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button onClick={handleSave} disabled={saving} variant="contained" sx={athenaDarkPrimaryButtonSx}>
          {t("common.save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExternalIdEditModal;
